import { Injectable } from '@angular/core';
import { createColumnDataType } from '@y42/column-type';
import {
  AbstractContentfulStateService,
  ContentfulGroupByTypesEnum,
  ContentfulGroupedItem,
  IContentfulTransformationType,
} from '@y42/contentful';
import { Entry } from 'contentful';
import groupBy from 'lodash/groupBy';
import { TransformationStepModel, TransformationTypeModel } from '../state/models/transformation-type.model';

@Injectable({ providedIn: 'root' })
export class TransformationTypesStateService extends AbstractContentfulStateService<
  IContentfulTransformationType,
  TransformationTypeModel
> {
  constructor() {
    super('transformationType', 'fields.order', 1000, (item: Entry<IContentfulTransformationType>) => ({
      id: item.fields.id,
      order: item.fields.order,
      title: item.fields.title,
      logo: item.fields.logo,
      icon: item.fields.icon.fields.file.url,
      iconDarkMode: item.fields.iconDarkMode.fields.file.url,
      color: item.fields.color,
      colorDarkMode: item.fields.colorDarkMode,
      portColor: item.fields.portColor,
      portColorDarkMode: item.fields.portColorDarkMode,
      description: item.fields.description,
      manipulationType: item.fields.manipulationType
        ? {
            name: item.fields.manipulationType.fields.name,
            order: item.fields.manipulationType.fields.order,
            icon: item?.fields?.manipulationType?.fields?.icon?.fields.file.url || '', // TODO: ???
          }
        : {
            name: 'Others',
            order: 0,
            icon: '',
          },
      columnType: item.fields.columnType
        ? createColumnDataType({
            name: item.fields.columnType.fields.displayName,
            icon: item?.fields?.columnType?.fields?.logo?.fields.file.url,
            color: item.fields.columnType.fields.color,
            columnType: item?.fields?.columnType?.fields?.type,
          })
        : createColumnDataType({ name: 'Others' }),
      articleId: item.fields.articleId,
      status: item.fields.status,
      isLowCode: item.fields.isLowCode,
    }));
  }

  findById(id: string): TransformationTypeModel | undefined {
    return this.state.entityState?.find((type: TransformationTypeModel) => type.id === id);
  }

  hasTableManipulation(step: TransformationStepModel): boolean {
    if (this.state.entityState?.length === 0) {
      return false;
    }
    const manipulationType = this.state.entityState?.find(
      (type: TransformationTypeModel) => type.id === step.transformationType,
    )?.manipulationType;
    return manipulationType?.name === 'Table Manipulations';
  }

  groupByType(
    items: TransformationTypeModel[],
    type: ContentfulGroupByTypesEnum,
  ): Array<ContentfulGroupedItem<TransformationTypeModel>> {
    if (items.length > 0) {
      const grouped = groupBy(items, (t: TransformationTypeModel) => {
        if (type === ContentfulGroupByTypesEnum.ColumnType) {
          return t?.columnType?.name;
        } else if (type === ContentfulGroupByTypesEnum.ManipulationType) {
          return t?.manipulationType?.name;
        }
        return '';
      });
      const groupedArray = Object.keys(grouped).map(
        (k) =>
          new ContentfulGroupedItem<TransformationTypeModel>(
            grouped[k][0][type]?.['name'],
            grouped[k][0][type]?.['icon'],
            grouped[k],
            type === ContentfulGroupByTypesEnum.ColumnType ? grouped[k][0].columnType?.color || '' : '',
            type === ContentfulGroupByTypesEnum.ColumnType ? grouped[k][0].columnType?.columnType || null : null,
            grouped[k][0].manipulationType?.order || 0,
          ),
      );
      return groupedArray;
    }
    return [];
  }
}
